import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  AutocompleteRenderInputParams,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  Stack,
  Chip,
  Divider,
  Paper,
  Modal,
  Badge,
  Tooltip,
  Link,
} from '@mui/material';
import {
  countryListWithCode,
  MARKET_FINDER_COLUMN_METADATA,
  MARKET_FINDER_COLUMN_METADATA_ORDER,
  MONTHLY_BEST_MARKET_FINDER_LIMIT,
  SAVE_USER_DATA_LAMBDA_URL,
  SAVED_MARKETS_POPUP_MESSAGE_THRESHOLD,
  usStateListWithCode,
  macro_to_subgroups,
  macro_group_legends,
  sub_group_legends,
  subGroupToCountryCodes,
  COLOR_PRIMARY,
  exchangeRates,
  DEFAULT_SELECTED_COLUMNS_EXPLORER,
  MARKET_EXPLORER_EXPORT_LIMIT_ROW_MONTHLY,
} from 'src/const';
import { callApi } from 'src/core/callApi';
import { User } from 'src/interfaces/iuser';
import _ from 'lodash';
import {
  areArraysEqual,
  convertToUSD,
  convertCurrency,
  makeDownloadableBlob,
} from 'src/utils/functions';
import FilterBar from './FilterBar';
import SelectedFiltersDisplay from './SelectedFiltersDisplay';
import MarketDataGrid, { MarketDataRow } from './MarketDataGrid';
import { DUMMY_MARKET_FINDER_DATA } from './dymmy_markets_data';
import { useCanAccessExpertPlan, useCanAccessScalePlan } from 'src/hooks/useFeatureAccess';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingCard } from 'src/App/HOC';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { getUserInfo } from 'src/store/Dashboard/actions';
import AutohideSnackbar from 'src/App/Components/Alerts/AutohideSnackbar';
import styles from './style';
import { COLOR_PRIMARY_2, COLOR_PRIMARY_2_800 } from 'src/colors';
import BookmarkIcon from '@mui/icons-material/Bookmark';

import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import TooltipNew from 'src/App/HOC/Tooltips/TooltipNew';
import TutorialModal from 'src/App/Modals/TutorialModal';
import MarketExportModal from './MarketExportModal';
import { updateUserMarketExplorerRowDataExportUsage } from './helper';

// Define blur states
type BlurState = 'none' | 'blurMarket' | 'blurAll' | 'blurPartial';

// Define type for market data with blur state by intersecting with MarketDataRow
type MarketDataWithBlur = MarketDataRow & {
  _blurState: BlurState;
};

const filterIconStyle = {
  fontSize: '16px',
};

// ===============================
// Filter Options (Moved from FilterBar)
// ===============================
const regulationOptions = [
  { value: 'strict', label: 'Strict', helperText: '(Required short-term rental license)' },
  { value: 'lenient', label: 'Lenient', helperText: '(May or may not require license)' },
];
const marketSizeOptions = [
  { value: 'big', label: 'Large', helperText: '(Over 5,000 active listings)' },
  { value: 'medium', label: 'Medium', helperText: '(1,000-5,000 active listings)' },
  { value: 'small', label: 'Small', helperText: '(Less than 1,000 active listings)' },
];
const sortByOptions = [
  {
    value: 'supply',
    label: 'Most Listings',
    icon: <HomeOutlinedIcon sx={filterIconStyle} />,
    tooltip: 'Shows markets with the highest number of active short-term rental listings',
  },
  {
    value: 'rent_gap',
    label: 'Top Rental Arbitrage',
    icon: <AttachMoneyOutlinedIcon sx={filterIconStyle} />,
    tooltip:
      'Markets with high long-term rental and short-term rental differences - ideal for Airbnb Arbitrage.',
  },
  {
    value: 'short_term_rental_yield',
    label: 'Top Investments',
    icon: <AccountBalanceWalletOutlinedIcon sx={filterIconStyle} />,
    tooltip: 'Markets with high gross rental yields. High revenue and low property prices.',
  },
  // { value: 'revenue', label: 'Revenue', tooltip: '' },
  {
    value: 'yoy_supply_change',
    label: 'Getting Saturated',
    icon: <BarChartOutlinedIcon sx={filterIconStyle} />,
    tooltip: 'Markets with the highest year-over-year increase in Airbnb supply.',
  },
  {
    value: 'yoy_revenue_change',
    label: 'Up & Coming',
    icon: <RocketLaunchOutlinedIcon sx={filterIconStyle} />,
    tooltip:
      'Markets with the highest year-over-year increase in average short-term rental revenue.',
  },
];

const formatSortByOptions = (sortByOptions: any[], budgetType: 'buy' | 'rent') => {
  return sortByOptions; // Always return all options
  // return sortByOptions.filter(option => {
  //   if (budgetType === 'buy') {
  //     return option.value !== 'rent_gap';
  //   }
  //   if (budgetType === 'rent') {
  //     return option.value !== 'short_term_rental_yield';
  //   }
  //   return true;
  // });
};

// Transform country list and add US states
const countryOptions = (() => {
  const options: any[] = [];

  Object.entries(macro_to_subgroups).forEach(([macro, subgroups]) => {
    const prefixedMacroValue = `macro-${macro}`;
    const macroOption = {
      value: prefixedMacroValue,
      label: macro_group_legends[macro],
      isExpandable: true, // Macros are always expandable to show subgroups or direct countries
      parentValue: null as string | null,
      isChild: false,
      level: 0,
    };
    options.push(macroOption);
    const seenValuesForMacro = new Set(); // Deduplication at the macro level

    if (subgroups.length === 1 && subgroups[0] === macro) {
      // Case: Macro is its own subgroup (e.g., NA: ['NA'])
      // Countries are direct children of the macro.
      const countryCodes = Array.from(new Set(subGroupToCountryCodes[macro] || []));
      countryCodes.forEach(countryCode => {
        const prefixedCountryValue = `country-${countryCode}`;
        if (seenValuesForMacro.has(prefixedCountryValue)) return;
        seenValuesForMacro.add(prefixedCountryValue);

        const country = countryListWithCode.find(c => c.code === countryCode);
        const isUS = countryCode === 'US';
        const countryLabel = country ? country.label.trim() : countryCode;

        const countryOption = {
          value: prefixedCountryValue,
          label: countryLabel,
          isExpandable: isUS,
          parentValue: prefixedMacroValue, // Parent is the macro itself
          isChild: true,
          level: 1, // Level 1, directly under macro
        };
        options.push(countryOption);

        if (isUS) {
          usStateListWithCode.forEach(state => {
            const prefixedStateValue = `state-${state.code}`;
            if (seenValuesForMacro.has(prefixedStateValue)) return;
            seenValuesForMacro.add(prefixedStateValue);
            options.push({
              value: prefixedStateValue,
              label: state.label,
              isExpandable: false,
              parentValue: prefixedCountryValue, // Parent is 'country-US'
              isChild: true,
              isState: true,
              level: 2, // Level 2, under 'country-US' (which is L1 relative to macro)
            });
          });
        }
      });
    } else {
      // Case: Macro has distinct subgroups (e.g., MEA: ['MENA', 'SSA'])
      // Filter out the macro itself if it's mistakenly listed as one of its own distinct subgroups
      subgroups
        .filter(subgroupItem => subgroupItem !== macro)
        .forEach(subgroupItem => {
          const prefixedSubgroupValue = `subgroup-${subgroupItem}`;
          const subgroupLabel = sub_group_legends[subgroupItem] || subgroupItem;
          const subgroupOption = {
            value: prefixedSubgroupValue,
            label: subgroupLabel,
            isExpandable: true, // Subgroups are expandable to show countries
            parentValue: prefixedMacroValue,
            isChild: false, // This is a category (parent for countries)
            level: 1,
          };
          options.push(subgroupOption);

          const countryCodes = Array.from(new Set(subGroupToCountryCodes[subgroupItem] || []));
          countryCodes.forEach(countryCode => {
            const prefixedCountryValue = `country-${countryCode}`;
            if (seenValuesForMacro.has(prefixedCountryValue)) return;
            seenValuesForMacro.add(prefixedCountryValue);

            const country = countryListWithCode.find(c => c.code === countryCode);
            const isUS = countryCode === 'US';
            const countryLabel = country ? country.label.trim() : countryCode;

            const countryOption = {
              value: prefixedCountryValue,
              label: countryLabel,
              isExpandable: isUS,
              parentValue: prefixedSubgroupValue, // Parent is the subgroup
              isChild: true,
              level: 2, // Level 2, under subgroup
            };
            options.push(countryOption);

            if (isUS) {
              usStateListWithCode.forEach(state => {
                const prefixedStateValue = `state-${state.code}`;
                if (seenValuesForMacro.has(prefixedStateValue)) return;
                seenValuesForMacro.add(prefixedStateValue);
                options.push({
                  value: prefixedStateValue,
                  label: state.label,
                  isExpandable: false,
                  parentValue: prefixedCountryValue, // Parent is 'country-US'
                  isChild: true,
                  isState: true,
                  level: 3, // Level 3, under 'country-US' (which is L2 relative to subgroup)
                });
              });
            }
          });
        });
    }
  });
  return options;
})();

// Helper to find label for a given value in options array
const findLabel = (
  options: { value: string; label: string }[], // Simplified, as specific child/parent logic isn't needed here anymore
  value: string
): string => {
  const option = options.find(opt => opt.value === value);
  return option ? option.label : '';
};

const CreditWarningModal = ({
  open,
  handleClose,
  handleOnClick,
  handleOnSampleClick,
}: {
  open: boolean;
  handleClose: () => void;
  handleOnClick: () => Promise<void>;
  handleOnSampleClick: (sample: string) => void;
}) => {
  const user: User = useSelector(state => (state as any).dashboard.user);
  const bestMarketFinderUsage = user.bestMarketFinderUsage;

  // YYYY-MM
  const currentYearMonthString = new Date().toISOString().slice(0, 7);
  const creditsUsed = bestMarketFinderUsage[currentYearMonthString] || 0;
  const totalCredits = MONTHLY_BEST_MARKET_FINDER_LIMIT[user.subscriptionPlan];
  const creditsRemaining = Math.max(0, totalCredits - creditsUsed);

  const isScaleUser = useCanAccessScalePlan();

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(max(300px, 90vw), 500px)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: '30px',
          borderRadius: '12px',
          outline: 'none',
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: '10px',
            right: '10px',
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
        <Typography variant="h6" sx={{ mb: '20px' }}>
          {(creditsRemaining > 0 || isScaleUser) && (
            <>
              Searching for markets will use one credit. Proceed to confirm your search. You have{' '}
              {creditsRemaining} credits remaining for this month.
            </>
          )}

          {creditsRemaining <= 0 && !isScaleUser && (
            <>
              You’ve reached your Expert plan limit for Market Explorer searches.{' '}
              <Link
                href={undefined}
                onClick={() => {
                  handleOnSampleClick('scale');
                  handleClose();
                }}
                sx={{
                  color: COLOR_PRIMARY_2,
                  cursor: 'pointer',
                }}
              >
                Upgrade to Scale Plan
              </Link>{' '}
              for 15 additional searches or wait for your limit to reset next month.
            </>
          )}
        </Typography>
        <Button
          variant="contained"
          disabled={creditsRemaining <= 0 && isScaleUser}
          onClick={() => {
            if (isScaleUser || creditsRemaining > 0) {
              handleOnClick();
            } else {
              handleOnSampleClick('scale');
            }
            handleClose();
          }}
          sx={{ float: 'right' }}
        >
          {isScaleUser || creditsRemaining > 0 ? 'Proceed' : 'Upgrade'}
        </Button>
      </Box>
    </Modal>
  );
};

// ===============================
// Main component
// ===============================

// Define labels for each filter type used in grouped chips
// const filterTypeLabels: { [key: string]: string } = {
//   location: 'Location',
//   regulation: 'Regulation',
//   marketSize: 'Market Size',
// };

// Helper to get parent macro for a subgroup
const getParentMacro = (
  subgroupValue: string, // This will be a prefixed value like "subgroup-SGCODE"
  allMacroToSubgroups: typeof macro_to_subgroups
): string | null => {
  const unprefixedSubgroup = subgroupValue.startsWith('subgroup-')
    ? subgroupValue.substring('subgroup-'.length)
    : subgroupValue;

  for (const [macro, subgroups] of Object.entries(allMacroToSubgroups)) {
    if (subgroups.includes(unprefixedSubgroup)) {
      return `macro-${macro}`; // Return the prefixed macro
    }
  }
  return null;
};

// Build regionOptions for the dropdown (macro and subgroups), flat array for grouping
const regionOptions = Object.entries(macro_to_subgroups).flatMap(([macro, subgroups]) => {
  const prefixedMacroValue = `macro-${macro}`;
  const macroOption: {
    value: string;
    label: string;
    isExpandable: boolean;
    parentValue: string | null;
    isChild: boolean;
  } = {
    value: prefixedMacroValue,
    label: macro_group_legends[macro],
    isExpandable: true, // Default to true, will be changed if no distinct subgroups
    parentValue: null as string | null,
    isChild: false,
  };

  // Only consider subgroups that are distinct from the macro itself
  const distinctSubgroups = subgroups.filter(sub => sub !== macro);

  if (distinctSubgroups.length === 0 && subgroups.includes(macro)) {
    // Case: NA: ['NA']. The macro itself is the only "group".
    // Macro is not expandable in the region filter as it has no distinct sub-options here.
    macroOption.isExpandable = false;
    return [macroOption]; // Only the macro option is returned
  } else {
    // Case: MEA: ['MENA', 'SSA'] or similar with distinct subgroups
    // Or a macro that has no subgroups listed at all (distinctSubgroups will be empty, subgroups.includes(macro) is false)
    // If distinctSubgroups is empty here, it means the macro has no listed sub-regions other than potentially itself (which was filtered out)
    // or no subregions at all. In such a case, it also shouldn't be expandable.
    if (distinctSubgroups.length === 0) {
      macroOption.isExpandable = false;
    }
    const subOptions = distinctSubgroups.map(sub => {
      const prefixedSubgroupValue = `subgroup-${sub}`;
      return {
        value: prefixedSubgroupValue,
        label: sub_group_legends[sub] || sub,
        isChild: true,
        parentValue: prefixedMacroValue,
        isExpandable: false, // Subgroups in region filter are not expandable
      };
    });
    return [macroOption, ...subOptions];
  }
});

const MarketExplorerPage = ({
  currency,
  handleOnSampleClick,
  isMobileScreen,
  OnBoardingSearchBox,
  setMarketComparisons,
  isLoggedIn,
  handleSidebarClick,
  handleSidebarListItemClick,
}: {
  currency: string;
  handleOnSampleClick: any;
  isMobileScreen: boolean;
  OnBoardingSearchBox: any;
  setMarketComparisons: any;
  isLoggedIn: boolean;
  handleSidebarClick?: any;
  handleSidebarListItemClick?: any;
}) => {
  const urlParams = useRef<URLSearchParams>(new URLSearchParams(window.location.search));
  const urlCountryCode = urlParams.current.get('countryCode');
  const currencySymbol = exchangeRates[currency]?.symbol || '$';

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [markets, setMarkets] = useState<any[]>([]);
  const [marketSearchInfo, setMarketSearchInfo] = useState<{
    searchName: string;
    countryCode: string;
  } | null>(null);
  const userCountryCode = useSelector(state => (state as any).dashboard.userCountryCode);
  const savedMarketsState = useSelector((state: any) => state.dashboard.savedMarkets);
  const {
    data: selectedList,
    loading: selectedListLoading,
    lastSavedMarketNames,
    openSavedMarketsCTAModal,
    isError: selectedListError,
  } = savedMarketsState;

  const [showSavedMarketLoadingError, setShowSavedMarketLoadingError] = useState(false);

  const [userPickedColumnFields, setUserPickedColumnFields] = useState<string[]>(
    DEFAULT_SELECTED_COLUMNS_EXPLORER
  );
  const user: User = useSelector(state => (state as any).dashboard.user);
  const currentYearMonthString = new Date().toISOString().slice(0, 7);
  // const marketExplorerRowDataExportUsage = user.marketExplorerRowDataExportUsage[currentYearMonthString] || 0;

  useEffect(() => {
    const userStrategy = user.userStrategy?.toLowerCase();

    if (userStrategy === 'buy') {
      const newUserPickedColumns = [...userPickedColumnFields];
      newUserPickedColumns.includes('rent_gap') &&
        newUserPickedColumns.splice(newUserPickedColumns.indexOf('rent_gap'), 1);
      !newUserPickedColumns.includes('short_term_rental_yield') &&
        newUserPickedColumns.push('short_term_rental_yield');

      setUserPickedColumnFields(newUserPickedColumns);
    } else if (userStrategy === 'rent') {
      const newUserPickedColumns = [...userPickedColumnFields];
      newUserPickedColumns.includes('short_term_rental_yield') &&
        newUserPickedColumns.splice(newUserPickedColumns.indexOf('short_term_rental_yield'), 1);
      !newUserPickedColumns.includes('rent_gap') && newUserPickedColumns.push('rent_gap');

      setUserPickedColumnFields(newUserPickedColumns);
    }
  }, [user]);

  const defaultMarketConfig = useMemo(
    () => ({
      region: [],
      location: [
        urlCountryCode ? `country-${urlCountryCode.toUpperCase()}` : `country-${userCountryCode}`,
      ],
      regulation: ['lenient'],
      marketSize: [],
      budget: {
        type: 'buy' as 'buy' | 'rent',
        range: { min: 0, max: 0 },
      },
      sortBy: 'supply',
    }),
    [userCountryCode, urlCountryCode]
  );

  const [currencSearchedMarket, setCurrencSearchedMarket] = useState<{
    region: string[];
    location: string[];
    regulation: string[];
    marketSize: string[];
    budget: {
      type: 'buy' | 'rent';
      range: { min: number; max: number };
    };
    sortBy: string;
  }>({
    region: defaultMarketConfig.region,
    location: defaultMarketConfig.location,
    regulation: defaultMarketConfig.regulation,
    marketSize: defaultMarketConfig.marketSize,
    budget: defaultMarketConfig.budget,
    sortBy: defaultMarketConfig.sortBy,
  });

  // State for filters - selectedLocations now holds country codes and state codes
  const [selectedLocations, setSelectedLocations] = useState<string[]>(
    defaultMarketConfig.location
  );
  const [selectedRegulations, setSelectedRegulations] = useState<string[]>(
    defaultMarketConfig.regulation
  );
  const [selectedMarketSizes, setSelectedMarketSizes] = useState<string[]>(
    defaultMarketConfig.marketSize
  );
  const [selectedSortBy, setSelectedSortBy] = useState<string>(defaultMarketConfig.sortBy);
  const [selectedBudget, setSelectedBudget] = useState<{
    type: 'buy' | 'rent';
    range: { min: number; max: number };
  }>(defaultMarketConfig.budget);

  const [creditDeductModalOpen, setCreditDeductModalOpen] = useState<boolean>(false);

  const [currentRequestType, setCurrentRequestType] = useState<'free' | 'premium'>('free');

  const isFreeUser = !useCanAccessExpertPlan();

  const dispatch = useDispatch();

  const [filtersChanged, setFiltersChanged] = useState(false);

  const [selectedRegions, setSelectedRegions] = useState<string[]>(defaultMarketConfig.region);

  const [showTutorial, setShowTutorial] = useState<boolean>(false);

  const [isUserLoaded, setIsUserLoaded] = useState<boolean>(false);

  const [showExportCreditWarningModal, setShowExportCreditWarningModal] = useState<boolean>(false);

  const isScaleUser = useCanAccessScalePlan();

  // Helper function to escape CSV cell content
  const escapeCsvCell = (cell: any): string => {
    const cellString = String(cell === null || cell === undefined ? '' : cell);
    const specialCharsRegex = /[,"\n\r]/;
    if (specialCharsRegex.test(cellString)) {
      return `"${cellString.replace(/"/g, '""')}"`;
    }
    return cellString;
  };

  // Helper function to format cell values for CSV export
  const formatCellValueForExport = useCallback(
    (
      value: any,
      metadata:
        | { unit: string | null; is_currency: boolean; data_type: string; display_name: string }
        | undefined
    ): string => {
      if (value === 'locked') return 'Paid plan required';
      if (value === null || value === undefined || value === '-') return '-';

      let formattedValue: string | number = value;
      let isCurrencyField = metadata?.is_currency;
      if (metadata && (metadata.data_type === 'number' || metadata.data_type === 'integer')) {
        try {
          const numberValue = Number(value);
          if (!isNaN(numberValue)) {
            if (isCurrencyField) {
              const valueInSelectedCurrency = convertCurrency(
                String(currency),
                Number(numberValue),
                true
              );
              formattedValue = valueInSelectedCurrency.toFixed(2);
            } else if (metadata?.unit === '%') {
              formattedValue = numberValue.toFixed(1); // Keep as number, e.g., 80.0 or 75.5
            } else {
              formattedValue = numberValue; // For other numbers
            }
          } else {
            formattedValue = '-';
          }
        } catch (e) {
          formattedValue = '-';
        }
      } else {
        formattedValue = String(value);
      }
      return String(formattedValue);
    },
    [currency]
  );

  // Process markets data (defined after helpers, before handleExport)
  const processedMarkets = useMemo((): MarketDataWithBlur[] => {
    let effectiveMarkets = [...markets];
    if (marketSearchInfo) {
      const lockedMarketRow: Omit<MarketDataRow, 'rank'> & { rank: number | string } = {
        rank: 'locked',
        name: marketSearchInfo.searchName,
        short_term_rental_yield: 'locked',
        rent_gap: 'locked',
        occupancy_rate: 'locked',
        adr: 'locked',
        revenue: 'locked',
        supply: 'locked',
        regulation: 'locked',
        yoy_supply_change: 'locked',
        yoy_revenue_change: 'locked',
      };
      effectiveMarkets.unshift(lockedMarketRow as MarketDataRow);
    }
    return effectiveMarkets.map(market => {
      const hasLockedValues = Object.values(market).some(value => value === 'locked');
      return {
        ...market,
        _blurState: hasLockedValues ? 'blurAll' : 'none',
      } as MarketDataWithBlur;
    });
  }, [markets, marketSearchInfo]);

  const handleExportRows = (isPremium: boolean = false) => {
    const headers: string[] = [];
    const dataRows: string[][] = [];

    const currentYearMonthString = new Date().toISOString().slice(0, 7);
    const creditsUsed = user.marketExplorerRowDataExportUsage[currentYearMonthString] || 0;
    const creditsRemaining = MARKET_EXPLORER_EXPORT_LIMIT_ROW_MONTHLY - creditsUsed;

    headers.push('Rank', 'Name');
    // Use userPickedColumnFields, filter out 'save'
    const exportableKeys = userPickedColumnFields.filter(key => key !== 'save');
    exportableKeys.forEach(key => {
      const columnMeta = MARKET_FINDER_COLUMN_METADATA[key];
      let headerName = columnMeta?.display_name || key;
      if (columnMeta?.is_currency) {
        headerName += ` (${currency})`;
      } else if (columnMeta?.unit === '%') {
        headerName += ' (%)';
      }
      headers.push(headerName);
    });

    const freeExportKeys = ['regulation', 'supply', 'occupancy_rate', 'adr'];

    (!isPremium ? processedMarkets : processedMarkets.slice(0, creditsRemaining)).forEach(
      market => {
        const row: string[] = [
          escapeCsvCell(formatCellValueForExport(market.rank, undefined)),
          escapeCsvCell(formatCellValueForExport(market.name, undefined)),
        ];
        exportableKeys.forEach(key => {
          if (!isPremium && !freeExportKeys.includes(key)) {
            row.push(escapeCsvCell(isScaleUser ? 'Unlock to View' : 'Scale Plan Required'));
          } else {
            const metadata = MARKET_FINDER_COLUMN_METADATA[key];
            row.push(escapeCsvCell(formatCellValueForExport(market[key], metadata)));
          }
        });
        dataRows.push(row);
      }
    );

    const csvContent = [headers.join(','), ...dataRows.map(dataRow => dataRow.join(','))].join(
      '\n'
    );

    makeDownloadableBlob(csvContent, 'my_markets_comparison', false, true);

    isPremium &&
      isScaleUser &&
      updateUserMarketExplorerRowDataExportUsage(
        Math.min(creditsRemaining, processedMarkets.length),
        dispatch
      );
  };

  // handleExport (defined after processedMarkets and helpers)
  const handleExport = () => {
    setShowExportCreditWarningModal(true);
  };

  const parseSearchConfigKey = (searchConfig: any) => {
    // Search Config
    // {"country_codes":["IN","CA","US"],"us_state_codes":["AR"],"loc_group_codes":["AS"],"regulations":["lenient"],"market_sizes":["big","medium"]}
    // Parsing Rule:
    // demoResultKey = "c-IN-US_s-CA-NY_r-lenient_size-medium-big".toLowerCase()
    // c follows country-code
    // s follows state-code
    // r follows regulation
    // size follows market-size

    const countryCodes = (searchConfig.country_codes || [])
      .map(code => code.toLowerCase())
      .join('-');
    const usStateCodes = (searchConfig.us_state_codes || [])
      .map(code => code.toLowerCase())
      .join('-');
    const locGroupCodes = (searchConfig.loc_group_codes || [])
      .map(code => code.toLowerCase())
      .join('-');
    const regulations = (searchConfig.regulations || []).map(code => code.toLowerCase()).join('-');
    const marketSizes = (searchConfig.market_sizes || []).map(code => code.toLowerCase()).join('-');

    return `loc-${locGroupCodes}_c-${countryCodes}_s-${usStateCodes}_r-${regulations}_size-${marketSizes}`.toLowerCase();
  };

  const parseSearchConfigFromKey = () => {
    const searchConfigFromLocalStorage = localStorage.getItem('last-market-explorer-search-config');
    const searchConfig = searchConfigFromLocalStorage
      ? JSON.parse(searchConfigFromLocalStorage || '{}')
      : null;

    if (!searchConfig) {
      return;
    }

    setSelectedRegions(searchConfig.selectedRegions);
    setSelectedLocations(searchConfig.selectedLocations);
    setSelectedRegulations(searchConfig.selectedRegulations);
    setSelectedMarketSizes(searchConfig.selectedMarketSizes);
    setSelectedSortBy(searchConfig.selectedSortBy);
    setSelectedBudget(searchConfig.selectedBudget);

    setCurrencSearchedMarket({
      region: searchConfig.selectedRegions,
      location: searchConfig.selectedLocations,
      regulation: searchConfig.selectedRegulations,
      marketSize: searchConfig.selectedMarketSizes,
      budget: searchConfig.selectedBudget,
      sortBy: searchConfig.selectedSortBy,
    });
  };

  const saveSearchConfig = async (searchConfig: any) => {
    const searchConfigKey = parseSearchConfigKey(searchConfig);
    // Save the search config...
    const response = await callApi(
      SAVE_USER_DATA_LAMBDA_URL,
      { body: { marketExplorerSearchHistory: searchConfigKey } },
      true,
      false,
      '',
      'POST',
      true
    );
    if (response && response.message) {
      console.log('Search config saved successfully');
    }
  };

  const fetchMarkets = useCallback(
    async (deductCredit: boolean = false, isFilterOnlyChange: boolean = false) => {
      try {
        setFiltersChanged(false);
        const requestBody: any = {
          lock_type: 'v2',
        };

        setIsLoading(true);
        setMarkets([]);

        // Handle Region Body
        if (selectedRegions.length > 0) {
          // selectedRegions now contains prefixed macro or sub-group codes
          requestBody.loc_group_codes = selectedRegions.map(r => {
            if (r.startsWith('macro-')) return r.substring('macro-'.length);
            if (r.startsWith('subgroup-')) return r.substring('subgroup-'.length);
            return r; // Should not happen if logic is correct
          });
        }

        // Handle Location Body
        if (selectedLocations.length > 0) {
          const countryCodeArray: Set<string> = new Set();
          const usStatesArray: Set<string> = new Set();
          const regionCodeArray: Set<string> = new Set();
          for (const location of selectedLocations) {
            // location is prefixed, e.g., country-US, state-CA
            if (location.startsWith('state-')) {
              usStatesArray.add(location.substring('state-'.length));
              countryCodeArray.add('US'); // US states imply US country
            } else if (location.startsWith('country-')) {
              countryCodeArray.add(location.substring('country-'.length));
            } else if (location.startsWith('macro-') || location.startsWith('subgroup-')) {
              regionCodeArray.add(location.split('-')[1]);
            }
          }
          requestBody.country_codes = Array.from(countryCodeArray);
          requestBody.us_state_codes = Array.from(usStatesArray);
          requestBody.loc_group_codes = Array.from(regionCodeArray);
        }

        // Handle Regulation Body
        if (selectedRegulations.length > 0) {
          requestBody.regulations = selectedRegulations;
        }

        // Handle Market Size Body
        if (selectedMarketSizes.length > 0) {
          requestBody.market_sizes = selectedMarketSizes;
        }

        // Handle Sort By Body
        if (selectedSortBy) {
          requestBody.sort_by = selectedSortBy;
        }

        let isPremiumRequest = deductCredit || isFilterOnlyChange;
        requestBody.is_premium_request = isPremiumRequest;

        setCurrentRequestType(isPremiumRequest ? 'premium' : 'free');

        const searchHistory = user.marketExplorerSearchHistory;
        if (searchHistory.length > 0) {
          const currentSearchConfigKey = parseSearchConfigKey(requestBody);
          if (searchHistory.includes(currentSearchConfigKey)) {
            requestBody.is_premium_request = true;
            setCurrentRequestType('premium');
            isPremiumRequest = true;
          }
        }

        if (!isPremiumRequest && selectedSortBy !== 'supply') {
          setFiltersChanged(_ => true);
          requestBody.sort_by = 'supply';
        }

        if (selectedBudget.range.min > 0 || selectedBudget.range.max > 0) {
          const minValue =
            currency === 'USD'
              ? selectedBudget.range.min
              : Math.round(convertToUSD(currency, selectedBudget.range.min));
          const maxValue =
            currency === 'USD'
              ? selectedBudget.range.max
              : Math.round(convertToUSD(currency, selectedBudget.range.max));

          requestBody.budget = {
            type: selectedBudget.type,
            range: [minValue, maxValue],
          };
        }

        const URL = 'https://niyu2lp99j.execute-api.us-east-2.amazonaws.com/default/getBestMarkets';
        const response = await callApi(URL, { body: requestBody }, true, false, '', 'POST', true);

        if (response && response.message && response.message.markets) {
          setMarkets(response.message.markets);
          localStorage.setItem(
            'last-market-explorer-search-config',
            JSON.stringify({
              selectedRegions: selectedRegions,
              selectedLocations: selectedLocations,
              selectedRegulations: selectedRegulations,
              selectedMarketSizes: selectedMarketSizes,
              selectedSortBy: selectedSortBy,
              selectedBudget: selectedBudget,
            })
          );

          if (
            deductCredit &&
            !user.marketExplorerSearchHistory.includes(parseSearchConfigKey(requestBody))
          ) {
            saveSearchConfig({
              country_codes: requestBody.country_codes,
              us_state_codes: requestBody.us_state_codes,
              loc_group_codes: requestBody.loc_group_codes,
              regulations: requestBody.regulations,
              market_sizes: requestBody.market_sizes,
            });
          }
          setCurrencSearchedMarket({
            region: selectedRegions,
            location: selectedLocations,
            regulation: selectedRegulations,
            marketSize: selectedMarketSizes,
            budget: selectedBudget,
            sortBy: selectedSortBy,
          });
          if (deductCredit && response.message?.markets?.length > 0) {
            callApi(
              SAVE_USER_DATA_LAMBDA_URL,
              { queryParams: { best_market_finder_usage: true } },
              true,
              false,
              '',
              'GET',
              true
            ).then(() => {
              dispatch(getUserInfo());
            });
          }
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching markets:', error);
        setIsLoading(false);
      }
    },
    [
      selectedRegions,
      selectedLocations,
      selectedRegulations,
      selectedMarketSizes,
      selectedSortBy,
      selectedBudget,
      currency,
      dispatch,
      setCurrencSearchedMarket,
      setCurrentRequestType,
      setFiltersChanged,
      setIsLoading,
      setMarkets,
      user,
      // Assuming callApi, convertToUSD, SAVE_USER_DATA_LAMBDA_URL, getUserInfo are stable
    ]
  );

  useEffect(() => {
    if (urlCountryCode) {
      urlParams.current.delete('countryCode');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [urlCountryCode]);

  useEffect(() => {
    const isFiltersNotChanged =
      JSON.stringify({
        region: currencSearchedMarket.region,
        location: currencSearchedMarket.location,
        regulation: currencSearchedMarket.regulation,
        marketSize: currencSearchedMarket.marketSize,
      }) ===
      JSON.stringify({
        region: selectedRegions,
        location: selectedLocations,
        regulation: selectedRegulations,
        marketSize: selectedMarketSizes,
      });

    if (
      currentRequestType === 'premium' &&
      isFiltersNotChanged &&
      selectedSortBy !== currencSearchedMarket.sortBy
    ) {
      fetchMarkets(false, true);
    } else if (
      isFiltersNotChanged &&
      JSON.stringify(selectedBudget) !== JSON.stringify(currencSearchedMarket.budget)
    ) {
      fetchMarkets(false, true);
    } else if (!isFiltersNotChanged) {
      fetchMarkets(false);
    } else if (selectedSortBy !== currencSearchedMarket.sortBy) {
      setFiltersChanged(true);
    }
  }, [
    selectedSortBy,
    selectedRegions,
    selectedLocations,
    selectedRegulations,
    selectedMarketSizes,
    selectedBudget,
    currencSearchedMarket,
    currentRequestType,
    isFreeUser,
    user,
  ]);

  useEffect(() => {
    if (marketSearchInfo && marketSearchInfo.countryCode) {
      setSelectedLocations([marketSearchInfo.countryCode]);
    }
  }, [marketSearchInfo]);

  useEffect(() => {
    setShowSavedMarketLoadingError(selectedListError);
  }, [selectedListError]);

  useEffect(() => {
    if (user.email?.toLowerCase() !== 'trial' || !isLoggedIn) {
      setIsUserLoaded(true);
    }
    !isUserLoaded && fetchMarkets(false);
  }, [user]);

  useEffect(() => {
    parseSearchConfigFromKey();
  }, []);

  const onFilterChangeAccessCheckMiddleware = (val: string | string[], actionFunction) => {
    if (isFreeUser) {
      handleOnSampleClick('expert');
    } else {
      actionFunction(val);
    }
  };

  // Handlers for FilterDropdown (Moved from FilterBar)
  // Updated handler for country/state selection
  const handleLocationsChange = (newSelection: string[]) => {
    // newSelection contains prefixed values like country-US, state-NY
    let updatedSelection = [...newSelection];

    const includesUSCountry = newSelection.includes('country-US');
    const includesStates = newSelection.some(val => val.startsWith('state-'));

    // If 'country-US' is selected AND specific states are selected, prioritize states by removing 'country-US'
    if (includesUSCountry && includesStates) {
      updatedSelection = updatedSelection.filter(val => val !== 'country-US');
    }
    // If 'country-US' was just added and no states were previously selected OR
    // if 'country-US' is selected and the user is deselecting the last state.
    else if (includesUSCountry) {
      // If 'country-US' is selected, ensure no individual states are in the selection.
      // This handles both adding 'country-US' (clearing states) and ensuring states are cleared if 'country-US' remains.
      updatedSelection = updatedSelection.filter(
        val => !val.startsWith('state-') || val === 'country-US'
      );
    }
    // If states are selected, but 'country-US' is not, ensure 'country-US' is not present.
    // This primarily handles the case where states are selected first, or 'country-US' is deselected while states remain.
    else if (!includesUSCountry && includesStates) {
      updatedSelection = updatedSelection.filter(val => val !== 'country-US');
    }

    // Specific scenario: User just added a state, and 'country-US' was NOT previously selected.
    // Ensure 'country-US' is not automatically added.
    const justAddedState =
      newSelection.some(loc => loc.startsWith('state-')) &&
      !selectedLocations.some(loc => loc.startsWith('state-'));
    if (
      justAddedState &&
      !selectedLocations.includes('country-US') &&
      updatedSelection.includes('country-US')
    ) {
      updatedSelection = updatedSelection.filter(val => val !== 'country-US');
    }

    // Specific scenario: User just added 'country-US', and states WERE previously selected.
    // The logic above (else if (includesUSCountry)) should handle clearing states.

    setSelectedLocations(updatedSelection);
  };
  const handleRegulationsChange = (val: string[] | string) => {
    if (Array.isArray(val)) setSelectedRegulations(val);
  };
  const handleMarketSizesChange = (val: string[] | string) => {
    if (Array.isArray(val)) setSelectedMarketSizes(val);
  };
  const handleSortByChange = (val: string[] | string) => {
    if (typeof val === 'string') {
      setSelectedSortBy(val);
    }
  };

  const handleBudgetChange = (values: { min: number; max: number }) => {
    setSelectedBudget(prev => ({ ...prev, range: values }));
  };

  const handleBudgetModeChange = (newMode: 'buy' | 'rent') => {
    setSelectedBudget(prev => ({
      type: newMode,
      range: { min: 0, max: 0 }, // Reset range when changing mode
    }));
  };

  // Individual filter removal handlers
  const handleRemoveLocation = (valueToRemove: string) => {
    setSelectedLocations(prev => prev.filter(loc => loc !== valueToRemove));
  };

  const handleRemoveRegulation = (valueToRemove: string) => {
    setSelectedRegulations(prev => prev.filter(reg => reg !== valueToRemove));
  };

  const handleRemoveMarketSize = (valueToRemove: string) => {
    setSelectedMarketSizes(prev => prev.filter(size => size !== valueToRemove));
  };

  const handleRegionsChange = (currentFullSelection: string[]) => {
    const finalSelection = new Set<string>();

    const newSelectedMacros = new Set<string>();
    const newSelectedSubregions = new Set<string>();

    currentFullSelection.forEach(item => {
      const unprefixedItemForMacroCheck = item.startsWith('macro-')
        ? item.substring('macro-'.length)
        : item;

      if (macro_to_subgroups[unprefixedItemForMacroCheck] && item.startsWith('macro-')) {
        newSelectedMacros.add(item);
      } else if (item.startsWith('subgroup-')) {
        const parentMacroPrefixed = getParentMacro(item, macro_to_subgroups);
        if (parentMacroPrefixed) {
          newSelectedSubregions.add(item);
        }
      }
    });

    newSelectedMacros.forEach(macro => finalSelection.add(macro));

    newSelectedSubregions.forEach(subregion => {
      const parentMacroPrefixed = getParentMacro(subregion, macro_to_subgroups);
      if (!parentMacroPrefixed || !newSelectedMacros.has(parentMacroPrefixed)) {
        finalSelection.add(subregion);
      }
    });

    setSelectedRegions(Array.from(finalSelection));
  };

  const handleRemoveRegion = (valueToRemove: string) => {
    setSelectedRegions(prev => prev.filter(region => region !== valueToRemove));
  };

  const SavedMarketsButton = () => {
    const isButtonHighlighted =
      lastSavedMarketNames.length >= SAVED_MARKETS_POPUP_MESSAGE_THRESHOLD;
    return (
      <Badge
        badgeContent={lastSavedMarketNames.length < 10 ? lastSavedMarketNames.length : '9+'}
        invisible={!isButtonHighlighted}
        color="primary"
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: COLOR_PRIMARY_2,
            color: '#fff',
            border: '1.5px solid #fff',
          },
        }}
      >
        <Button
          variant="outlined"
          startIcon={
            <BookmarkIcon style={{ color: isButtonHighlighted ? 'white' : COLOR_PRIMARY_2 }} />
          }
          sx={{
            ...styles.commonButtonSx,
            ...(!isButtonHighlighted ? styles.outlinedButtonSx : styles.containedButtonSx),
            ...(!isButtonHighlighted
              ? { color: '#000', backgroundColor: '#fff' }
              : { backgroundColor: COLOR_PRIMARY_2 }),
          }}
          disableElevation
          onClick={() => {
            handleSidebarClick && handleSidebarClick('main');
            handleSidebarListItemClick && handleSidebarListItemClick(0);
          }}
        >
          Saved Markets
        </Button>
      </Badge>
    );
  };

  return (
    <>
      <Grid container sx={{ mt: '30px', mb: '30px', ...(isMobileScreen ? { px: '16px' } : {}) }}>
        <Grid item xs={12} sx={{ mb: '10px' }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: '24px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
            }}
          >
            Market Explorer
            <TooltipNew
              title=""
              content="Click to see the 3 minutes tutorial video on how to use this feature."
            >
              <IconButton color="primary" onClick={() => setShowTutorial(true)}>
                <HelpOutlineRoundedIcon />
              </IconButton>
            </TooltipNew>
          </Typography>
        </Grid>

        {filtersChanged && (
          <AutohideSnackbar
            message="Please unlock the search to enable filter changes."
            type="warning"
            autoHideDuration={5000}
            handleOnCloseSideEffect={() => {
              setFiltersChanged(false);
            }}
          />
        )}

        <Grid item xs={12}>
          <FilterBar
            selectedRegions={selectedRegions}
            regionOptions={regionOptions}
            handleRegionsChange={handleRegionsChange}
            selectedLocations={selectedLocations}
            selectedRegulations={selectedRegulations}
            selectedMarketSizes={selectedMarketSizes}
            selectedBudget={{
              buy: selectedBudget.type === 'buy' ? selectedBudget.range : undefined,
              rent: selectedBudget.type === 'rent' ? selectedBudget.range : undefined,
            }}
            selectedBudgetMode={selectedBudget.type}
            handleLocationsChange={handleLocationsChange}
            handleBudgetChange={(type: string, values: { min: number; max: number }) => {
              setSelectedBudget({
                type: type as 'buy' | 'rent',
                range: values,
              });
            }}
            handleBudgetModeChange={handleBudgetModeChange}
            handleRegulationsChange={handleRegulationsChange}
            handleMarketSizesChange={handleMarketSizesChange}
            countryOptions={countryOptions}
            regulationOptions={regulationOptions}
            marketSizeOptions={marketSizeOptions}
            onFilterChangeAccessCheckMiddleware={onFilterChangeAccessCheckMiddleware}
            openSearchModal={() =>
              !isFreeUser ? setCreditDeductModalOpen(true) : handleOnSampleClick('expert')
            }
            isMobileScreen={isMobileScreen}
            OnBoardingSearchBox={OnBoardingSearchBox}
            setMarketSearchInfo={setMarketSearchInfo}
            setMarketComparisons={setMarketComparisons}
            isFreeUser={isFreeUser}
            currency={currency}
            handleSidebarClick={handleSidebarClick}
            handleSidebarListItemClick={handleSidebarListItemClick}
            onExport={handleExport}
            userPickedColumnFields={userPickedColumnFields}
            onUserPickedFieldsChange={setUserPickedColumnFields}
          />
        </Grid>

        {/* Display Selected Filters using the new component */}
        {/* Pass individual filter data and removal handlers */}
        <SelectedFiltersDisplay
          selectedRegions={selectedRegions}
          regionOptions={regionOptions}
          handleRemoveRegion={handleRemoveRegion}
          selectedLocations={selectedLocations}
          selectedRegulations={selectedRegulations}
          selectedMarketSizes={selectedMarketSizes}
          selectedSortBy={selectedSortBy}
          selectedBudget={selectedBudget}
          countryOptions={countryOptions}
          regulationOptions={regulationOptions}
          marketSizeOptions={marketSizeOptions}
          sortByOptions={formatSortByOptions(sortByOptions, selectedBudget.type)}
          findLabel={findLabel}
          handleRemoveLocation={handleRemoveLocation}
          handleRemoveRegulation={handleRemoveRegulation}
          handleRemoveMarketSize={handleRemoveMarketSize}
          handleRemoveBudget={() =>
            setSelectedBudget({ type: selectedBudget.type, range: { min: 0, max: 0 } })
          }
          handleSortByChange={handleSortByChange}
          isMobileScreen={isMobileScreen}
          isFreeUser={isFreeUser}
          handleOnSampleClick={handleOnSampleClick}
          currency={currency}
          userPickedColumnFields={userPickedColumnFields}
        />

        {/* Rest of the page content */}
        {/* Render the Data Grid */}
        <Grid item xs={12}>
          {/* Pass processed data to the grid */}
          {isLoading ? (
            <LoadingCard height={150} type="bars" />
          ) : (
            <MarketDataGrid
              data={processedMarkets}
              metadata={MARKET_FINDER_COLUMN_METADATA}
              isFreeUser={isFreeUser}
              isLoggedIn={isLoggedIn}
              handleOnSampleClick={handleOnSampleClick}
              currency={currency}
              selectedBudget={selectedBudget}
              currentRequestType={currentRequestType}
              openCreditDeductModal={() => setCreditDeductModalOpen(true)}
              selectedList={selectedListLoading ? 'loading' : selectedList}
              selectedListError={selectedListError}
              showSavedMarketLoadingError={showSavedMarketLoadingError}
              setShowSavedMarketLoadingError={setShowSavedMarketLoadingError}
              handleSidebarClick={handleSidebarClick}
              handleSidebarListItemClick={handleSidebarListItemClick}
              selectedSortBy={selectedSortBy}
              userPickedColumnFields={userPickedColumnFields}
            />
          )}
        </Grid>
      </Grid>

      <Box
        sx={{
          position: 'fixed',
          bottom: isMobileScreen ? '80px' : '20px',
          right: isMobileScreen ? '15px' : '20px',
          zIndex: 99999,
        }}
      >
        {SavedMarketsButton()}
      </Box>

      {creditDeductModalOpen && (
        <CreditWarningModal
          open={creditDeductModalOpen}
          handleClose={() => setCreditDeductModalOpen(false)}
          handleOnClick={() => fetchMarkets(true)}
          handleOnSampleClick={handleOnSampleClick}
        />
      )}

      {showTutorial && (
        <TutorialModal
          open={showTutorial}
          onClose={() => setShowTutorial(false)}
          type="market_explorer"
          noProgressBar={true}
        />
      )}

      {showExportCreditWarningModal && (
        <MarketExportModal
          open={showExportCreditWarningModal}
          handleClose={() => setShowExportCreditWarningModal(false)}
          handleExportRows={handleExportRows}
          handleOnSampleClick={handleOnSampleClick}
          numberOfRows={processedMarkets.length}
          handleExportRowsFree={() => handleExportRows(false)}
          setCreditDeductModalOpen={setCreditDeductModalOpen}
          currentRequestType={currentRequestType}
        />
      )}
    </>
  );
};

export default MarketExplorerPage;
